/**
 * Date utilities for photo grouping and display
 */

import type { Photo } from '@/types';
import type { GalleryItem } from './missedCapturesCalculator';

export interface DayGroup {
  dateKey: string;        // "2025-12-08" for comparison/keying
  displayDate: string;    // "Today", "Yesterday", "Sunday 8 December"
  photoCount: number;
  startIndex: number;     // First photo index in this group
  startRow: number;       // First row containing photos from this day (computed externally)
}

/**
 * Get start of day (midnight) for a given date
 */
function startOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Format a date as YYYY-MM-DD for comparison/keying
 * Uses local timezone (not UTC) to match display dates
 */
function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format date relative to today in English
 * - "Today"
 * - "Yesterday"
 * - "Monday 8 December" (within last 7 days), short: "Mon 8 Dec"
 * - "8 December 2025" (older), short: "8 Dec 2025"
 */
export function formatRelativeDate(date: Date, locale = 'en-GB', short = false): string {
  const now = new Date();
  const today = startOfDay(now);
  const targetDay = startOfDay(date);
  const diffMs = today.getTime() - targetDay.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 0 && diffDays < 7) {
    // Full: "Monday 8 December", Short: "Mon 8 Dec"
    return date.toLocaleDateString(locale, {
      weekday: short ? 'short' : 'long',
      day: 'numeric',
      month: short ? 'short' : 'long'
    });
  }
  // Full: "8 December 2025", Short: "8 Dec 2025"
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: short ? 'short' : 'long',
    year: 'numeric'
  });
}

/**
 * Format a date as relative time ago (e.g., "3s ago", "4m ago", "3h ago")
 */
export function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Format an interval in seconds to human-readable format (e.g., "30s", "1m", "1h")
 */
export function formatInterval(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

/**
 * Convert Firestore Timestamp or Date-like object to Date
 */
export function toDate(timestamp: any): Date | null {
  if (!timestamp) return null;
  try {
    // Firestore Timestamp
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    // Already a Date
    if (timestamp instanceof Date) {
      return timestamp;
    }
    // Timestamp-like object with seconds
    if (typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
    }
    // Try parsing as date string/number
    return new Date(timestamp);
  } catch {
    return null;
  }
}

// ============================================
// Device Timezone Formatting
// ============================================

/**
 * Format a timestamp in the device's timezone (or browser timezone as fallback)
 * @param timestamp - Firestore Timestamp, Date, or timestamp-like object
 * @param timezone - IANA timezone string (e.g. "Europe/Rome"), undefined for browser timezone
 * @param options - Intl.DateTimeFormatOptions for formatting
 */
export function formatInDeviceTimezone(
  timestamp: any,
  timezone: string | undefined,
  options: Intl.DateTimeFormatOptions = {}
): string {
  const date = toDate(timestamp);
  if (!date) return '';

  const formatOptions: Intl.DateTimeFormatOptions = {
    ...options,
    timeZone: timezone, // undefined = browser timezone (fallback)
  };

  return date.toLocaleString('en-GB', formatOptions);
}

/**
 * Format time only (HH:mm:ss) in device timezone
 */
export function formatTimeInDeviceTimezone(
  timestamp: any,
  timezone: string | undefined
): string {
  return formatInDeviceTimezone(timestamp, timezone, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Format date and time in device timezone
 */
export function formatDateTimeInDeviceTimezone(
  timestamp: any,
  timezone: string | undefined
): string {
  return formatInDeviceTimezone(timestamp, timezone, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Group photos by day
 * Assumes photos are already sorted by capturedAt (descending - newest first)
 * Returns array of day groups with photo counts and start indices
 */
export function groupPhotosByDay(photos: Photo[], columnCount: number): DayGroup[] {
  if (photos.length === 0) return [];

  const groups: DayGroup[] = [];
  let currentDateKey: string | null = null;
  let currentGroup: DayGroup | null = null;

  photos.forEach((photo, index) => {
    const date = toDate(photo.capturedAt);
    if (!date) return;

    const dateKey = toDateKey(date);

    if (dateKey !== currentDateKey) {
      // Start new group
      if (currentGroup) {
        groups.push(currentGroup);
      }

      currentDateKey = dateKey;
      currentGroup = {
        dateKey,
        displayDate: formatRelativeDate(date),
        photoCount: 1,
        startIndex: index,
        startRow: Math.floor(index / columnCount),
      };
    } else if (currentGroup) {
      // Add to current group
      currentGroup.photoCount++;
    }
  });

  // Push last group
  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups;
}

/**
 * Find which day group is visible at a given scroll position
 */
export function findVisibleDayGroup(
  dayGroups: DayGroup[],
  scrollTop: number,
  rowHeight: number
): DayGroup | null {
  if (dayGroups.length === 0) return null;

  const currentRow = Math.floor(scrollTop / rowHeight);

  // Find the group that contains this row
  for (let i = dayGroups.length - 1; i >= 0; i--) {
    if (currentRow >= dayGroups[i].startRow) {
      return dayGroups[i];
    }
  }

  return dayGroups[0];
}

// ============================================
// React-Virtuoso GroupedVirtuoso data structures
// ============================================

/**
 * Single row of photos for virtuoso rendering
 */
export interface PhotoRow {
  photos: Photo[];
  /** Original indices in the photos array for each photo (for shift-click selection) */
  indices: number[];
}

/**
 * Day group with photos organized into rows for virtuoso
 */
export interface DayGroupWithRows {
  dateKey: string;
  displayDate: string;
  /** Short version for small screens (e.g., "Mon 8 Dec") */
  displayDateShort: string;
  photoCount: number;
  rows: PhotoRow[];
  /** Number of photos not shown (for collapsed view) */
  remainingCount?: number;
  /** Index of first photo in this group (for navigation) */
  firstPhotoIndex?: number;
}

/**
 * Complete data structure for GroupedVirtuoso
 */
export interface VirtuosoGroupedData {
  groups: DayGroupWithRows[];
  groupCounts: number[];  // Number of rows per group
  allRows: PhotoRow[];    // Flat array of all rows for itemContent
}

/**
 * Photo paired with its original index in the photos array
 */
interface PhotoWithIndex {
  photo: Photo;
  originalIndex: number;
}

/**
 * Group photos into rows of N columns, with original indices for each photo
 */
function chunkPhotosIntoRows(photosWithIndices: PhotoWithIndex[], columnCount: number): PhotoRow[] {
  const rows: PhotoRow[] = [];

  for (let i = 0; i < photosWithIndices.length; i += columnCount) {
    const chunk = photosWithIndices.slice(i, i + columnCount);
    rows.push({
      photos: chunk.map(p => p.photo),
      indices: chunk.map(p => p.originalIndex),
    });
  }

  return rows;
}

/**
 * Group photos by day and organize into rows for GroupedVirtuoso
 * Returns data structure compatible with GroupedVirtuoso API
 * Photos within each day are reversed (oldest first, newest last)
 */
export function groupPhotosForVirtuoso(photos: Photo[], columnCount: number): VirtuosoGroupedData {
  if (photos.length === 0) {
    return { groups: [], groupCounts: [], allRows: [] };
  }

  const groups: DayGroupWithRows[] = [];
  const allRows: PhotoRow[] = [];

  let currentDateKey: string | null = null;
  let currentPhotosWithIndices: PhotoWithIndex[] = [];

  photos.forEach((photo, index) => {
    const date = toDate(photo.capturedAt);
    if (!date) return;

    const dateKey = toDateKey(date);

    if (dateKey !== currentDateKey) {
      // Save previous group if exists
      if (currentDateKey !== null && currentPhotosWithIndices.length > 0) {
        // Reverse to show oldest first within each day
        const reversed = [...currentPhotosWithIndices].reverse();
        const rows = chunkPhotosIntoRows(reversed, columnCount);
        const firstDate = toDate(currentPhotosWithIndices[0].photo.capturedAt);
        groups.push({
          dateKey: currentDateKey,
          displayDate: firstDate ? formatRelativeDate(firstDate) : currentDateKey,
          displayDateShort: firstDate ? formatRelativeDate(firstDate, 'en-GB', true) : currentDateKey,
          photoCount: currentPhotosWithIndices.length,
          rows,
        });
        allRows.push(...rows);
      }

      // Start new group
      currentDateKey = dateKey;
      currentPhotosWithIndices = [{ photo, originalIndex: index }];
    } else {
      // Add to current group
      currentPhotosWithIndices.push({ photo, originalIndex: index });
    }
  });

  // Push last group
  if (currentDateKey !== null && currentPhotosWithIndices.length > 0) {
    // Reverse to show oldest first within each day
    const reversed = [...currentPhotosWithIndices].reverse();
    const rows = chunkPhotosIntoRows(reversed, columnCount);
    const firstDate = toDate(currentPhotosWithIndices[0].photo.capturedAt);
    groups.push({
      dateKey: currentDateKey,
      displayDate: firstDate ? formatRelativeDate(firstDate) : currentDateKey,
      displayDateShort: firstDate ? formatRelativeDate(firstDate, 'en-GB', true) : currentDateKey,
      photoCount: currentPhotosWithIndices.length,
      rows,
    });
    allRows.push(...rows);
  }

  // Calculate group counts (number of rows per group)
  const groupCounts = groups.map(g => g.rows.length);

  return { groups, groupCounts, allRows };
}

/**
 * Group photos by day with collapsed view showing one row of photos + "Show X more"
 * Days in expandedDays Set show all photos
 * Photos within each day are reversed (oldest first, newest last)
 */
export function groupPhotosForVirtuosoCollapsed(
  photos: Photo[],
  columnCount: number,
  _maxPhotosPerDay: number = 5, // unused, kept for API compatibility
  expandedDays: Set<string> = new Set()
): VirtuosoGroupedData {
  if (photos.length === 0) {
    return { groups: [], groupCounts: [], allRows: [] };
  }

  const groups: DayGroupWithRows[] = [];
  const allRows: PhotoRow[] = [];

  let currentDateKey: string | null = null;
  let currentPhotosWithIndices: PhotoWithIndex[] = [];

  const saveGroup = () => {
    if (currentDateKey !== null && currentPhotosWithIndices.length > 0) {
      const totalCount = currentPhotosWithIndices.length;
      const isExpanded = expandedDays.has(currentDateKey);

      // Keep original order: newest first within each day (photos arrive sorted desc)
      let visiblePhotosWithIndices: PhotoWithIndex[];
      let remainingCount: number;

      const twoRowsCount = columnCount * 2;
      if (isExpanded || totalCount <= twoRowsCount) {
        // Show all photos if expanded or fits in two rows
        visiblePhotosWithIndices = currentPhotosWithIndices;
        remainingCount = 0;
      } else {
        // Show first two rows minus 1 photo (newest), last cell is "more" placeholder
        visiblePhotosWithIndices = currentPhotosWithIndices.slice(0, twoRowsCount - 1);
        remainingCount = totalCount - (twoRowsCount - 1);
      }

      const rows = chunkPhotosIntoRows(visiblePhotosWithIndices, columnCount);
      const firstDate = toDate(currentPhotosWithIndices[0].photo.capturedAt);

      groups.push({
        dateKey: currentDateKey,
        displayDate: firstDate ? formatRelativeDate(firstDate) : currentDateKey,
        displayDateShort: firstDate ? formatRelativeDate(firstDate, 'en-GB', true) : currentDateKey,
        photoCount: totalCount,
        rows,
        remainingCount: remainingCount > 0 ? remainingCount : undefined,
        firstPhotoIndex: currentPhotosWithIndices[0].originalIndex,
      });
      allRows.push(...rows);
    }
  };

  photos.forEach((photo, index) => {
    const date = toDate(photo.capturedAt);
    if (!date) return;

    const dateKey = toDateKey(date);

    if (dateKey !== currentDateKey) {
      // Save previous group
      saveGroup();

      // Start new group
      currentDateKey = dateKey;
      currentPhotosWithIndices = [{ photo, originalIndex: index }];
    } else {
      // Add to current group
      currentPhotosWithIndices.push({ photo, originalIndex: index });
    }
  });

  // Push last group
  saveGroup();

  // Calculate group counts (number of rows per group)
  const groupCounts = groups.map(g => g.rows.length);

  return { groups, groupCounts, allRows };
}

// ============================================
// Gallery Items (Photos + Missed Captures)
// ============================================

/**
 * Single row of gallery items for rendering
 */
export interface GalleryItemRow {
  items: GalleryItem[];
  startIndex: number;  // Global index of first item in this row
}

/**
 * Day group with gallery items organized into rows
 */
export interface DayGroupWithGalleryItems {
  dateKey: string;
  displayDate: string;
  itemCount: number;
  photoCount: number;
  missedCount: number;
  rows: GalleryItemRow[];
}

/**
 * Complete data structure for gallery with mixed items
 */
export interface GalleryGroupedData {
  groups: DayGroupWithGalleryItems[];
  groupCounts: number[];
  allRows: GalleryItemRow[];
}

/**
 * Group gallery items into rows of N columns
 */
function chunkGalleryItemsIntoRows(items: GalleryItem[], columnCount: number, globalStartIndex: number): GalleryItemRow[] {
  const rows: GalleryItemRow[] = [];

  for (let i = 0; i < items.length; i += columnCount) {
    rows.push({
      items: items.slice(i, i + columnCount),
      startIndex: globalStartIndex + i,
    });
  }

  return rows;
}

/**
 * Group gallery items (photos + missed captures) by day
 * Items must be pre-sorted by timestamp (descending - newest first)
 */
export function groupGalleryItemsForDisplay(items: GalleryItem[], columnCount: number): GalleryGroupedData {
  if (items.length === 0) {
    return { groups: [], groupCounts: [], allRows: [] };
  }

  const groups: DayGroupWithGalleryItems[] = [];
  const allRows: GalleryItemRow[] = [];

  let currentDateKey: string | null = null;
  let currentItems: GalleryItem[] = [];
  let currentStartIndex = 0;

  items.forEach((item, index) => {
    const date = item.timestamp;
    if (!date) return;

    const dateKey = toDateKey(date);

    if (dateKey !== currentDateKey) {
      // Save previous group if exists
      if (currentDateKey !== null && currentItems.length > 0) {
        const rows = chunkGalleryItemsIntoRows(currentItems, columnCount, currentStartIndex);
        const photoCount = currentItems.filter(i => i.type === 'photo').length;
        const missedCount = currentItems.filter(i => i.type === 'missed').length;
        groups.push({
          dateKey: currentDateKey,
          displayDate: formatRelativeDate(currentItems[0].timestamp),
          itemCount: currentItems.length,
          photoCount,
          missedCount,
          rows,
        });
        allRows.push(...rows);
      }

      // Start new group
      currentDateKey = dateKey;
      currentItems = [item];
      currentStartIndex = index;
    } else {
      // Add to current group
      currentItems.push(item);
    }
  });

  // Push last group
  if (currentDateKey !== null && currentItems.length > 0) {
    const rows = chunkGalleryItemsIntoRows(currentItems, columnCount, currentStartIndex);
    const photoCount = currentItems.filter(i => i.type === 'photo').length;
    const missedCount = currentItems.filter(i => i.type === 'missed').length;
    groups.push({
      dateKey: currentDateKey,
      displayDate: formatRelativeDate(currentItems[0].timestamp),
      itemCount: currentItems.length,
      photoCount,
      missedCount,
      rows,
    });
    allRows.push(...rows);
  }

  // Calculate group counts (number of rows per group)
  const groupCounts = groups.map(g => g.rows.length);

  return { groups, groupCounts, allRows };
}
